import { tl } from 'common/services/i18nService';
import { isNullOrUndefined } from 'common/util/utils';
import moment from 'moment';
import { SIMPLE_DATE_FORMAT_STRING_NO_SECONDS } from 'common/store/common/constants';
import store from '../store';
import { RootState } from '../store/rootReducer';
import { Committee, getCommitteeById } from './committeeService';
import { GradingStatus, WorkflowState } from '../store/context/types';
import { CandidateLevelWorkflowState } from '../store/common/constants';
import {
    hasCurrentUserEvaluatorPrivilege,
    isGradingReadyForParty,
} from './permissions/PermissionService';
import {
    getCurrentCandidateLevelWorkflowState,
    GradingPartyDocument,
    getGradesForRound,
    EvaluatorGrade,
    getEvaluationRoundByCommitteeId,
    EvaluationRound, getPartyCommitteeIdForContext, getLatestEvaluationRound,
} from './gradingPartyService';
import { isSharedMarks } from './setMarksServices';
import { CommitteeType } from '../store/assessment/constants';

// values used for marks_mode and confirmation_mode
export enum GRADING_MODE {
    INDIVIDUAL = 'individual',
    COLLABORATIVE = 'collaborative',
}

export enum GRADING_LEVEL {
    TEST = 'test',
    CANDIDATE = 'candidate',
}

export enum CandidateLevelActions {
    CONFIRM='CONFIRM',
    ALIGN='ALIGN',
    SHARE='SHARE',
    REVERT_TO_PRIVATE='REVERT_TO_PRIVATE',
    REVERT_TO_SHARED='REVERT_TO_SHARED',

    SHARE_ON_BEHALF = 'SHARE_ON_BEHALF',
    UNSHARE_ON_BEHALF = 'UNSHARE_ON_BEHALF',
    CONFIRM_ON_BEHALF = 'CONFIRM_ON_BEHALF',
    UNCONFIRM_ON_BEHALF = 'UNCONFIRM_ON_BEHALF',

    REOPEN_MARKING = 'REOPEN_MARKING'
}

export const isTestFinished = (): boolean => {
    const { assessment } = (store.getState() as RootState);
    const endTimeMoment = moment.utc(assessment.assessmentRunEndTime, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
    return assessment.assessmentRunGradingStatus !== GradingStatus.CREATED
        && assessment.assessmentRunGradingStatus !== GradingStatus.SCHEDULED
        && assessment.assessmentRunGradingStatus !== GradingStatus.ACTIVE
        && endTimeMoment.local().isBefore(moment());
};

export const getMarksModeForTestOrCommittee = (committeeId?: number): string => {
    const { assessment } = (store.getState() as RootState);
    if (assessment.committees && committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        // the server sends the actual value,
        // on committee if defined or test if no setting defined on the committee
        return committee && committee.marksMode;
    }
    return assessment.marksMode;
};

export const getConfirmationModeForTestOrCommittee = (committeeId?: number): string => {
    const { assessment } = (store.getState() as RootState);
    if (assessment.committees && committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        // the server sends the actual value,
        // on committee if defined or test if no setting defined on the committee
        return committee && committee.confirmationMode;
    }
    return assessment.confirmationMode;
};

export const getGradingLevelForTestOrCommittee = (committeeId?: number): string => {
    const { assessment } = (store.getState() as RootState);
    if (assessment.committees && committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        // the server sends the actual value,
        // on committee if defined or test if no setting defined on the committee
        return committee && committee.gradingLevel;
    }
    return assessment && assessment.gradingLevel;
};

export const getQuestionAssignmentAccessLevelForTestOrCommittee = (committeeId?: number): string => {
    const { assessment } = (store.getState() as RootState);
    if (assessment.committees && committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        // if it's not set on the committee, let it use the assessment run value
        if (committee && committee.gradingQuestionAssignmentAccessLevel) {
            return committee.gradingQuestionAssignmentAccessLevel;
        }
    }
    return assessment && assessment.gradingQuestionAssignmentAccessLevel;
};

export const getQuestionAssignmentAdminLevelForTestOrCommittee = (committeeId?: number): string => {
    const { assessment } = (store.getState() as RootState);
    if (assessment.committees && committeeId > 0) {
        const committee: Committee = getCommitteeById(committeeId);
        // if it's not set on the committee, let it use the assessment run value
        if (committee && committee.gradingQuestionAssignmentAdminLevel) {
            return committee.gradingQuestionAssignmentAdminLevel;
        }
    }
    return assessment && assessment.gradingQuestionAssignmentAdminLevel;
};

export const isPrivateMarksInWorkflowState = (): boolean => {
    const { context } = (store.getState() as RootState);
    const { workflowInfo } = context;
    const workflowStateObject = Object.keys(WorkflowState);
    const userWorkflowState = workflowInfo.evaluatorState ? workflowInfo.evaluatorState : workflowInfo.plannerState;

    return workflowStateObject.indexOf(userWorkflowState?.toUpperCase())
        <= workflowStateObject.indexOf(WorkflowState.OWN_GRADING_CONFIRMED_WAITING.toUpperCase());
};

export const isOtherGraderNotSharedInWorkflowState = (): boolean => {
    const { context } = (store.getState() as RootState);
    const { workflowInfo } = context;

    return workflowInfo
        && workflowInfo.evaluatorState === WorkflowState.OWN_GRADING_CONFIRMED_WAITING;
};

export const areAllGradesAtLeastShared = (isCandidateLevelGrading: boolean, candidateWorkflowState: CandidateLevelWorkflowState): boolean => {
    const { context } = (store.getState() as RootState);
    const { workflowInfo } = context;
    if (isCandidateLevelGrading) {
        const currIndex = Object.keys(CandidateLevelWorkflowState).indexOf(candidateWorkflowState);
        const requiredIndex = Object.keys(CandidateLevelWorkflowState).indexOf(CandidateLevelWorkflowState.SHARED_CONFLICTS);
        return currIndex >= requiredIndex;
    }
    const userWorkflowState = workflowInfo.evaluatorState ? workflowInfo.evaluatorState : workflowInfo.plannerState;
    return Object.keys(WorkflowState).indexOf(userWorkflowState?.toUpperCase())
        >= Object.keys(WorkflowState).indexOf(WorkflowState.FINAL_GRADING_CONFLICTS.toUpperCase());
};

export const isGraderAtLeastPendingShareForCandidate = (candidateWorkflowState: CandidateLevelWorkflowState): boolean => {
    return Object.keys(CandidateLevelWorkflowState).indexOf(candidateWorkflowState)
        >= Object.keys(CandidateLevelWorkflowState).indexOf(CandidateLevelWorkflowState.PENDING_SHARE);
};

export const isCandidateInPrivateState = (candidateWorkflowState: CandidateLevelWorkflowState): boolean => {
    return candidateWorkflowState === CandidateLevelWorkflowState.NOT_STARTED
        || candidateWorkflowState === CandidateLevelWorkflowState.PRIVATE
        || candidateWorkflowState === CandidateLevelWorkflowState.PENDING_SHARE;
};

export const getCandidateActionForCandidateLevelWorkflowState = (candidateWorkflowState: CandidateLevelWorkflowState,
    singleGrader: boolean): CandidateLevelActions => {
    switch (candidateWorkflowState) {
    case CandidateLevelWorkflowState.NOT_STARTED:
    case CandidateLevelWorkflowState.PRIVATE:
        if (singleGrader || isSharedMarks()) {
            return CandidateLevelActions.CONFIRM;
        }
        return CandidateLevelActions.SHARE;
    case CandidateLevelWorkflowState.SHARED_CONFLICTS:
        return CandidateLevelActions.ALIGN;
    case CandidateLevelWorkflowState.SHARED:
        return CandidateLevelActions.CONFIRM;
    case CandidateLevelWorkflowState.PENDING_SHARE:
        return CandidateLevelActions.REVERT_TO_PRIVATE;
    case CandidateLevelWorkflowState.PENDING_CONFIRM:
        return CandidateLevelActions.REVERT_TO_SHARED;
    }
    return null;
};

/*eslint-disable*/
export const getGraderCandidateActionForGradingParty = (gradingPartyDocument: GradingPartyDocument,
                                                                 candidateCoveredByEvaluator: boolean,
                                                                 singleGrader: boolean,
                                                                 currentUserId: number): CandidateLevelActions => {
    // we shouldn't allow any candidate action if the candidate is not covered by the evaluator or
    // the test/committee is not grading in progress (either test ready for grading, committee ready for grading or the committee is appeals)
    if (!candidateCoveredByEvaluator || !isGradingReadyForParty(gradingPartyDocument)) {
        return null;
    }

    const candidateWorkflowState: CandidateLevelWorkflowState = getCurrentCandidateLevelWorkflowState(gradingPartyDocument, currentUserId);
    return getCandidateActionForCandidateLevelWorkflowState(candidateWorkflowState, singleGrader);
};

export const getPlannerCandidateActionForGradingParty = (gradingPartyDocument: GradingPartyDocument,
                                                            isPlanner: boolean, isSingleGrader: boolean): Set<CandidateLevelActions> => {
    const { assessment: { compositeInfo, isMultipleAttemptsParent } } = (store.getState() as RootState);
    const isCandidateLevelGrading = getGradingLevelForTestOrCommittee(gradingPartyDocument.committeeId) === GRADING_LEVEL.CANDIDATE;
    if (!isPlanner || !isCandidateLevelGrading){
        return new Set();
    }
    const availablePlannerActions: Set<CandidateLevelActions> = new Set();
    const contextCommitteeId: number = getPartyCommitteeIdForContext(gradingPartyDocument);
    const latestRound: EvaluationRound = getLatestEvaluationRound(gradingPartyDocument);
    let round: EvaluationRound;
    if (!isNullOrUndefined(contextCommitteeId) && contextCommitteeId > 0) {
        round = getEvaluationRoundByCommitteeId(gradingPartyDocument, contextCommitteeId);
    } else {
        // in case of fjord it will be the latest round so that there are candidate actions shown
        round = latestRound;
    }

    if (round.evaluationRound < latestRound.evaluationRound) {
        // if looking at a previous round, no actions even for planner
        return new Set();
    }

    const gradesForRound: Array<EvaluatorGrade> = getGradesForRound(gradingPartyDocument, round.evaluationRound);
    const allGradersAtMostPendingShare: boolean = gradesForRound.every((evaluatorGrade: EvaluatorGrade) => {
        return Object.keys(CandidateLevelWorkflowState).indexOf(evaluatorGrade.candidateLevelWorkflowState) <=
            Object.keys(CandidateLevelWorkflowState).indexOf(CandidateLevelWorkflowState.PENDING_SHARE);
    });
    const atLeastOneGraderSharedOrBelow: boolean = gradesForRound.some((evaluatorGrade: EvaluatorGrade) => {
        return evaluatorGrade.candidateLevelWorkflowState === CandidateLevelWorkflowState.SHARED ||
            evaluatorGrade.candidateLevelWorkflowState === CandidateLevelWorkflowState.SHARED_CONFLICTS
    });
    const allGradersSharedOrPendingConfirm: boolean =  gradesForRound.every((evaluatorGrade: EvaluatorGrade) => {
        return evaluatorGrade.candidateLevelWorkflowState === CandidateLevelWorkflowState.SHARED ||
        evaluatorGrade.candidateLevelWorkflowState === CandidateLevelWorkflowState.PENDING_CONFIRM;
    });
    const atLeastOneGraderPendingConfirm: boolean = gradesForRound.some((evaluatorGrade: EvaluatorGrade) => {
        return evaluatorGrade.candidateLevelWorkflowState === CandidateLevelWorkflowState.PENDING_CONFIRM
    });

    const allGradersConfirmed: boolean =  gradesForRound.every((evaluatorGrade: EvaluatorGrade) => {
        return evaluatorGrade.candidateLevelWorkflowState === CandidateLevelWorkflowState.CONFIRMED;
    });

    const isMarksShared = isSharedMarks();
    const isParentTest = !isMultipleAttemptsParent && compositeInfo && compositeInfo.children && compositeInfo.children.length > 0;
    // treating single grader separately (easier this way)
    if (isSingleGrader) {
        if (allGradersConfirmed) {
            if (!isParentTest) {
                availablePlannerActions.add(CandidateLevelActions.REOPEN_MARKING);
            }
        } else {
            availablePlannerActions.add(CandidateLevelActions.CONFIRM_ON_BEHALF);
        }

        return availablePlannerActions;
    }

    if (allGradersAtMostPendingShare) {
        if (isMarksShared) {
            availablePlannerActions.add(CandidateLevelActions.CONFIRM_ON_BEHALF);
        } else {
            availablePlannerActions.add(CandidateLevelActions.SHARE_ON_BEHALF);
        }
    }

    if (atLeastOneGraderSharedOrBelow && !isMarksShared) {
        availablePlannerActions.add(CandidateLevelActions.UNSHARE_ON_BEHALF);
    }

    if (allGradersSharedOrPendingConfirm) {
        availablePlannerActions.add(CandidateLevelActions.CONFIRM_ON_BEHALF);
    }

    if (atLeastOneGraderPendingConfirm) {
        if(!isMarksShared){
            availablePlannerActions.add(CandidateLevelActions.UNSHARE_ON_BEHALF);
        }
        availablePlannerActions.add(CandidateLevelActions.UNCONFIRM_ON_BEHALF);

    }

    if (allGradersConfirmed && !isParentTest) {
        availablePlannerActions.add(CandidateLevelActions.REOPEN_MARKING);
    }

    return availablePlannerActions;

};

export const getMarkingStatus = (currentWorkflowState: string) => {
    const {assessment, context } = (store.getState() as RootState);
    const { partyState } = context;
    const selectedCommitteeId = getPartyCommitteeIdForContext(partyState.gradingPartyDocument);

    switch (currentWorkflowState) {
        case WorkflowState.NOT_FINISHED:
            return tl('grading.status.active');
        case WorkflowState.NOT_READY_FOR_GRADING:
            return tl('grading.header.progress.notReadyForGrading');
        case WorkflowState.SINGLE_GRADER:
        case WorkflowState.READY_FOR_GRADING:
        case WorkflowState.OWN_GRADING_STARTED:
            const selectedCommittee: Committee = selectedCommitteeId > 0 ? getCommitteeById(selectedCommitteeId) : null;
            let statusCaption = tl('grading.header.primaryStatus.ready_for_grading');
            if (
                assessment.assessmentRunGradingStatus
                === GradingStatus.GRADING_IN_PROGRESS
                || (selectedCommittee
                && selectedCommittee.type === CommitteeType.reassessment
                && !selectedCommittee.gradingCompleted)
            ) {
                statusCaption = tl('grading.status.grading_in_progress');
            }
            let endTimeCaption;
            if (!isTestFinished() && assessment.assessmentRunGradingStatus !== GradingStatus.GRADED
                && getGradingLevelForTestOrCommittee(selectedCommitteeId) === GRADING_LEVEL.CANDIDATE) {
                endTimeCaption = tl('grading.header.secondaryStatus.not_finished').toLowerCase();
            }
            return statusCaption + (!isNullOrUndefined(endTimeCaption) ? ` (${endTimeCaption})` : '');
        case WorkflowState.OWN_GRADING_CONFIRMED_WAITING:
            return tl('grading.status.grading_in_progress');
        case WorkflowState.FINAL_GRADING_CONFIRMED_WAITING:
            return tl('grading.header.primaryStatus.youHaveConfirmed');
        case WorkflowState.FINAL_GRADING_CONFLICTS:
            return tl('grading.status.alignGrades');
        case WorkflowState.FINAL_GRADING_ALLOWED:
            const marksMode = getMarksModeForTestOrCommittee(selectedCommitteeId);
            if (marksMode === GRADING_MODE.COLLABORATIVE) {
                return tl('grading.status.grading_in_progress');
            }
            return tl('grading.status.alignedGrades');
        case WorkflowState.GRADING_COMPLETED:
            return tl('grading.status.graded');
    }
    return undefined;
};

export const getMarkingStatusForGradingWorkflow = (currentWorkflowState: string) => {
    const { context } = (store.getState() as RootState);
    const { workflowInfo } = context;

    if (!workflowInfo) {
        return getMarkingStatus(currentWorkflowState);
    }

    switch (currentWorkflowState) {
    case WorkflowState.NOT_FINISHED:
        return tl('grading.status.active');
    case WorkflowState.NOT_READY_FOR_GRADING:
    case WorkflowState.OWN_GRADING_STARTED:
        return tl('grading.header.primaryStatus.noMarking');
    case WorkflowState.SINGLE_GRADER:
    case WorkflowState.READY_FOR_GRADING:
    case WorkflowState.FINAL_GRADING_ALLOWED:
        return tl('grading.status.grading_in_progress');
    case WorkflowState.OWN_GRADING_CONFIRMED_WAITING:
    case WorkflowState.FINAL_GRADING_CONFIRMED_WAITING:
        return tl('grading.status.graded');
    default:
        return getMarkingStatus(currentWorkflowState);
    }
};

export const getProgressStatusForGradingWorkflow = (currentWorkflowState: string, languageCode = 'en', remainingTimeCaption?: string) => {
    const { context, session } = (store.getState() as RootState);
    const { workflowInfo, partyState } = context;

    if (!workflowInfo) {
        return undefined;
    }

    const gradedCandidatesCount = workflowInfo.numberOfGradedCandidatesForContext || 0;
    const totalCandidatesCount = workflowInfo.numberOfCandidatesForContext || 0;
    const allCandidatesMarked = totalCandidatesCount > 0 && gradedCandidatesCount === totalCandidatesCount;
    const currentEvaluatorLastEdited = partyState?.gradingPartyDocument?.phaseEvaluationGrades
        ?.find((evaluatorGrade: EvaluatorGrade) => evaluatorGrade.evaluatorId === session?.user?.id)
        ?.lastEdited;
    const confirmationDate = workflowInfo.finalConfirmationDate
        || partyState?.gradingPartyDocument?.finalGradeConfirmationDate
        || currentEvaluatorLastEdited;
    const confirmationMoment = confirmationDate
         ? moment.utc(confirmationDate, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS)
         : null;
    const formattedConfirmationDate = confirmationMoment?.isValid()
        ? confirmationMoment.locale(languageCode).format('HH:mm DD MMM YY')
        : null;

    switch (currentWorkflowState) {
    case WorkflowState.NOT_FINISHED:
        return remainingTimeCaption;
    case WorkflowState.NOT_READY_FOR_GRADING:
    case WorkflowState.OWN_GRADING_STARTED:
        return null;
    case WorkflowState.SINGLE_GRADER:
    case WorkflowState.READY_FOR_GRADING:
    case WorkflowState.FINAL_GRADING_ALLOWED:
        if (allCandidatesMarked) {
            return tl('grading.header.primaryStatus.allCandidatesQuestionsMarked');
        }
        return tl('grading.header.progress.gradingProgress', {
            gradedCandidatesCount,
            totalCandidatesCount,
        });
    case WorkflowState.OWN_GRADING_CONFIRMED_WAITING:
    case WorkflowState.FINAL_GRADING_CONFIRMED_WAITING:
    case WorkflowState.GRADING_COMPLETED:
        if (formattedConfirmationDate) {
            return tl('grading.header.progress.confirmedOn', {
                confirmationDate: formattedConfirmationDate,
            });
        }
        return null;
    default:
        return undefined;
    }
};

export const isGradingLockedForCandidate = (gradingPartyDocument: GradingPartyDocument): boolean => {
    const { assessment } = (store.getState() as RootState);
    const { isAutoDeliveryEnabled, autoDeliveryAllowMultipleSubmission, } = assessment;
    if (!isNullOrUndefined(gradingPartyDocument)
        && !isNullOrUndefined(gradingPartyDocument.deliveryDeadline)
        && isTestFinished()
        && isAutoDeliveryEnabled
        && autoDeliveryAllowMultipleSubmission) {
        const deliveryDeadlineMoment = moment.utc(gradingPartyDocument.deliveryDeadline);
        if (deliveryDeadlineMoment.local().isAfter(moment())) {
            return true;
        }
    }
    return false;
};

export const isGraderThatStillHasNotGradedCandidates = () => {
    if (!hasCurrentUserEvaluatorPrivilege()) {
        return false;
    }
    const { context, assessment } = (store.getState() as RootState);
    const { workflowInfo } = context;

    let submittedCandidateCount;

    if (assessment.committees) {
        const currentCommittee = assessment.committees.find((committee) => committee.committeeId === context.selectedCommitteeId);
        submittedCandidateCount = currentCommittee?.candidateCount;
    } else {
        submittedCandidateCount = workflowInfo.numberOfSubmissionsForContext;
    }

    return workflowInfo.numberOfGradedCandidatesForContext !== submittedCandidateCount;
};
